import { useEffect, useRef, useState } from "react";
import { container } from "tsyringe";
import { ArrowLeftIcon, ArrowPathIcon, CalculatorIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { SimulatorProduct, SimulatorRequest, SimulatorResult } from "common/types/Simulator";
import SimulatorApi from "api/wenImmo/SimulatorApi";
import Button, { EButtonVariant } from "components/elements/Button";
import Typography, { ITypo } from "components/elements/Typography";
import MessageBox from "components/elements/MessageBox";
import NavTabComponent from "components/elements/NavTabComponent";
import Form from "components/materials/Form";
import MuiSelectInputElement from "components/materials/Form/MuiSelectInputElement";
import CheckboxesInputElement from "components/materials/Form/CheckboxesInputElement";
import I18n from "components/materials/I18n";
import useHead from "hooks/useHead";
import { Link } from "react-router-dom";
import ModuleConfig from "configs/ModuleConfig";
import { Choice, money, NumberField, percent } from "./controls";
import ExportButtons from "./export/ExportButtons";
import { allocationLabels, options } from "./labels";
import Parameters from "./Parameters";
import Results from "./Results";
import classes from "./classes.module.scss";

const api = container.resolve(SimulatorApi);
const defaults = (): SimulatorRequest => ({
	budget: 100000,
	years: 10,
	dividendDecrease: 0.03,
	appreciation: 0.005,
	ownership: "full",
	ownershipYears: 10,
	allocation: "equal",
	lines: [],
	excludedCountries: [],
	excludedSectors: [],
	credit: { enabled: false, amount: 80000, rate: 0.035, years: 20, insurance: 0.003, type: "amortizing", deferMonths: 0, deferType: "partial" },
	tax: { enabled: false, marginalRate: 0.3, socialRate: 0.172, foreignShare: 0, foreignRate: 0.2, regime: "real", ifi: false, otherWealth: 0 },
});

export default function Simulator() {
	useHead({ title: "Simulateur SCPI · Wenimmo" });
	const [products, setProducts] = useState<SimulatorProduct[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [request, setRequest] = useState(defaults);
	const [result, setResult] = useState<SimulatorResult | null>(null);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	const [allocationMode, setAllocationMode] = useState<"equal" | "amount" | "shares" | "percent">("equal");
	const [pickerVersion, setPickerVersion] = useState(0);
	const focusPicker = useRef(false);
	useEffect(() => {
		focusPicker.current = false;
	}, [pickerVersion]);
	const revision = useRef(0);
	const load = () => {
		revision.current++;
		setLoading(true);
		setLoadError(false);
		setError("");
		setResult(null);
		api.products()
			.then((available) => {
				setProducts(available);
				setRequest((r) => ({ ...r, lines: r.lines.filter((line) => available.some((p) => p.productId === line.productId)) }));
			})
			.catch(() => setLoadError(true))
			.finally(() => setLoading(false));
	};
	useEffect(() => {
		load();
	}, []);
	const update = (patch: Partial<SimulatorRequest>) => {
		revision.current++;
		setRequest((r) => ({ ...r, ...patch }));
		if (patch.lines?.length === 0) {
			setResult(null);
			setStale(false);
		} else setStale(true);
		setError("");
	};
	useEffect(() => {
		if (!request.lines.length || loading || loadError) {
			setBusy(false);
			return;
		}
		let active = true;
		const current = revision.current;
		setBusy(true);
		const timer = setTimeout(() => {
			api.calculate(request)
				.then((response) => {
					if (active && revision.current === current) {
						setResult(response);
						setStale(false);
					}
				})
				.catch((e) => {
					if (active && revision.current === current)
						setError(
							e && typeof e === "object" && "message" in e
								? String(e.message)
								: "Le calcul est indisponible. Rechargez le catalogue pour réessayer.",
						);
				})
				.finally(() => {
					if (active && revision.current === current) setBusy(false);
				});
		}, 350);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [request, products, loading, loadError]);
	const excluded = (p: SimulatorProduct) =>
		!!p.simulatorData?.geography.some((e) => request.excludedCountries.includes(e.label) && e.weight > 0.05) ||
		!!p.simulatorData?.sectors.some((e) => request.excludedSectors.includes(e.label) && e.weight > 0.1);
	const selected = products.filter((p) => request.lines.some((l) => l.productId === p.productId));
	const choices = products.filter((p) => !selected.includes(p) && !excluded(p));
	const setMode = (mode: typeof allocationMode) => {
		setAllocationMode(mode);
		update({
			allocation: mode,
			lines: request.lines.map((l) => ({
				...l,
				value:
					mode === "shares"
						? result?.lines.find((row) => row.productId === l.productId)?.shares ?? 0
						: (mode === "percent" ? 100 : request.budget) / Math.max(1, request.lines.length),
			})),
		});
	};
	const add = (productId: string) => {
		focusPicker.current = true;
		setPickerVersion((v) => v + 1);
		update({
			lines: [
				...request.lines,
				{
					productId,
					value:
						request.allocation === "equal" || request.allocation === "shares"
							? 0
							: Math.max(0, (request.allocation === "percent" ? 100 : request.budget) - request.lines.reduce((s, l) => s + l.value, 0)),
				},
			],
		});
	};
	const filter = (key: "excludedCountries" | "excludedSectors", label: string, checked: boolean) =>
		update({ [key]: checked ? [...request[key], label] : request[key].filter((v) => v !== label) });
	const countExclusions = request.excludedCountries.length + request.excludedSectors.length;
	return (
		<I18n>
			<main className={classes["screen"]}>
				<header className={classes["header"]}>
					<Link className={classes["back"]} to={container.resolve(ModuleConfig).get().modules.pages.Products.props.path}>
						<ArrowLeftIcon width={18} /> Retour à la plateforme
					</Link>
					<div className={classes["title"]}>
						<h1>Simulateur SCPI</h1>
						<span>Wenimmo</span>
					</div>
					<Button
						variant={EButtonVariant.TEXT}
						icon={<ArrowPathIcon />}
						onClick={() => {
							setAllocationMode("equal");
							setPickerVersion((v) => v + 1);
							update(defaults());
						}}>
						Réinitialiser
					</Button>
				</header>
				<Form className={classes["root"]}>
					<div className={classes["workspace"]}>
						<aside className={classes["sidebar"]} aria-label="Configurer la simulation">
							<div className={classes["budget"]}>
								<NumberField label="Montant à investir (€)" value={request.budget} onChange={(budget) => update({ budget })} />
								<div className={classes["quick"]}>
									{[50000, 100000, 250000].map((budget) => (
										<Button
											key={budget}
											variant={request.budget === budget ? EButtonVariant.CONTAINED : EButtonVariant.OUTLINED}
											onClick={() => update({ budget })}>
											{budget / 1000} k€
										</Button>
									))}
								</div>
							</div>
							<NavTabComponent
								compact
								items={[
									{
										label: `Allocation (${selected.length})`,
										children: (
											<div className={classes["allocation"]}>
												{loading ? (
													<p role="status">Chargement…</p>
												) : loadError ? (
													<>
														<MessageBox type="warning" text="Catalogue indisponible." />
														<Button onClick={load}>Réessayer</Button>
													</>
												) : (
													<MuiSelectInputElement
														disablePortal={false}
														key={pickerVersion}
														className={classes["picker"]}
														name="scpi-picker"
														autoFocus={focusPicker.current}
														size="small"
														label="Ajouter une SCPI"
														placeholder="Rechercher dans votre catalogue"
														options={choices.map((p) => ({ id: p.productId, label: p.name }))}
														defaultValue={null}
														onChange={(_, option) => option && add(String(option.id))}
													/>
												)}
												{!selected.length ? (
													<div className={classes["emptySmall"]}>
														<Typography typo={ITypo.P_SMALL}>
															Sélectionnez vos SCPI dans le catalogue pour comparer les revenus.
														</Typography>
														<Typography typo={ITypo.CAPTION}>{choices.length} SCPI disponibles</Typography>
													</div>
												) : (
													<>
														<Choice
															label="Répartition"
															value={allocationMode}
															options={options(allocationLabels)}
															onChange={setMode}
														/>
														<div className={classes["selection"]}>
															{selected.map((p) => {
																const line = request.lines.find((l) => l.productId === p.productId)!;
																const computed =
																	!stale && !error ? result?.lines.find((l) => l.productId === p.productId) : undefined;
																return (
																	<article className={classes["selectedProduct"]} key={p.productId}>
																		<div className={classes["productHeading"]}>
																			<Typography typo={ITypo.P_MEDIUM_BOLD}>{p.name}</Typography>
																			<Button
																				variant={EButtonVariant.TEXT}
																				aria-label={`Retirer ${p.name}`}
																				icon={<XMarkIcon />}
																				onClick={() =>
																					update({ lines: request.lines.filter((l) => l.productId !== p.productId) })
																				}
																			/>
																		</div>
																		<div className={classes["productMeta"]}>
																			<span>{money(p.price)} / part</span>
																			<span>
													{p.distributionRateNet != null
														? `Taux net ${percent(p.distributionRateNet / 100)}`
														: p.distributionRate == null
															? "Taux inconnu"
															: `Taux brut ${percent(p.distributionRate / 100)}`}
																			</span>
																		</div>
																		{allocationMode === "equal" ? (
																			<div className={classes["productAmount"]}>
																				<strong>
																					{computed
																						? money(computed.invested)
																						: Number.isFinite(request.budget)
																							? money(request.budget / selected.length)
																							: "—"}
																				</strong>
																				<span>{computed ? `${computed.shares} parts` : "Budget affecté"}</span>
																			</div>
																		) : (
																			<NumberField
																				label={`Allocation ${p.name}${allocationMode === "percent" ? " (%)" : allocationMode === "shares" ? " (parts)" : " (€)"}`}
																				value={line.value}
																				step={allocationMode === "shares" ? 1 : 0.01}
																				onChange={(value) =>
																					update({
																						lines: request.lines.map((l) =>
																							l.productId === p.productId ? { ...l, value: value } : l,
																						),
																					})
																				}
																			/>
																		)}
																		{excluded(p) && (
																			<small className={classes["errorText"]}>
																				Dépasse une exclusion : retirez cette SCPI.
																			</small>
																		)}
																	</article>
																);
															})}
														</div>
														<Typography typo={ITypo.CAPTION}>Parts entières · Minimums contrôlés.</Typography>
													</>
												)}
											</div>
										),
									},
									{ label: "Paramètres", children: <Parameters request={request} selected={selected} update={update} /> },
									{
										label: `Filtres${countExclusions ? ` (${countExclusions})` : ""}`,
										children: (
											<div className={classes["fields"]}>
												<Typography typo={ITypo.P_SMALL}>
													Exclusion au-delà de 5 % par pays et 10 % par secteur. Les répartitions manquantes restent inconnues.
												</Typography>
												{(
													[
														["geography", "excludedCountries", "Pays"],
														["sectors", "excludedSectors", "Secteurs"],
													] as const
												).map(([dataKey, key, label]) => (
													<CheckboxesInputElement
														key={key}
														label={label}
														name={key}
														options={[...new Set(products.flatMap((p) => p.simulatorData?.[dataKey].map((e) => e.label) ?? []))]
															.sort((a, b) => a.localeCompare(b, "fr"))
															.map((label) => ({ value: label, label, checked: request[key].includes(label) }))}
														onChange={(e) => filter(key, e.target.value, e.target.checked)}
													/>
												))}
											</div>
										),
									},
								]}
							/>
						</aside>
						<section className={classes["resultsPane"]} aria-label="Résultats">
							{result && !error ? (
								<>
									<div className={stale ? classes["staleResult"] : classes["currentResult"]} aria-hidden={stale}>
										<Results
											result={result}
											products={selected}
											taxEnabled={request.tax.enabled}
											ownership={request.ownership}
											creditEnabled={request.credit.enabled}
											actions={<ExportButtons input={{ request, result, products: selected }} disabled={stale || busy} />}
										/>
									</div>
									{stale && (
										<div className={classes["recalculating"]} role="status">
											Actualisation…
										</div>
									)}
								</>
							) : error ? (
								<div role="alert" className={classes["empty"]}>
									<MessageBox type="warning" text={error} />
									<Button variant={EButtonVariant.TEXT} onClick={load}>
										Actualiser le catalogue
									</Button>
								</div>
							) : (
								<div className={classes["empty"]}>
									<CalculatorIcon width={32} />
									<Typography typo={ITypo.P_LARGE_BOLD}>{busy ? "Calcul en cours…" : "Votre projection"}</Typography>
									<Typography typo={ITypo.P_SMALL}>
										{busy
											? "Mise à jour avec vos paramètres."
											: "Ajoutez une SCPI pour voir ses revenus, la répartition et l’évolution du capital."}
									</Typography>
								</div>
							)}
						</section>
					</div>
					<div className={classes["footnote"]} role="status">
						{busy
							? "Calcul en cours…"
							: error
								? "Ajustez les paramètres pour recalculer."
								: result
									? "Résultats à jour · Projection indicative, sans garantie de rendement ou de liquidité."
									: "Calcul automatique · Simulation sans sauvegarde."}
					</div>
				</Form>
			</main>
		</I18n>
	);
}
